import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useCurrentOrganization } from '@/modules/organizations/hooks/use-organizations'
import { vacancySuggestionService } from '../infrastructure/vacancy-suggestion-service'
import { toast } from 'sonner'
import axios from 'axios'

export const vacancySuggestionKeys = {
  all: (orgId: string) => ['vacancy-suggestions', orgId] as const,
  list: (orgId: string, params?: { candidateId?: string; targetVacancyId?: string; status?: string }) =>
    ['vacancy-suggestions', orgId, 'list', params] as const,
  shortlist: (orgId: string, sourceApplicationId: string) =>
    ['vacancy-suggestions', orgId, 'shortlist', sourceApplicationId] as const,
  byId: (orgId: string, id: string) =>
    ['vacancy-suggestions', orgId, id] as const,
}

function extractErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { error?: string } | undefined
    return data?.error ?? fallback
  }
  return fallback
}

export function useVacancySuggestions(params?: {
  candidateId?: string
  targetVacancyId?: string
  status?: string
}) {
  const organization = useCurrentOrganization()
  return useQuery({
    queryKey: vacancySuggestionKeys.list(organization?.id ?? '', params),
    queryFn: () =>
      vacancySuggestionService.list(organization!.id, params),
    enabled: !!organization?.id,
  })
}

export function useVacancySuggestionShortlist(sourceApplicationId: string | undefined) {
  const organization = useCurrentOrganization()
  return useQuery({
    queryKey: vacancySuggestionKeys.shortlist(organization?.id ?? '', sourceApplicationId ?? ''),
    queryFn: () =>
      vacancySuggestionService.getShortlist(organization!.id, sourceApplicationId!),
    enabled: !!organization?.id && !!sourceApplicationId,
  })
}

export function useCompareVacancy() {
  const queryClient = useQueryClient()
  const organization = useCurrentOrganization()

  return useMutation({
    mutationFn: ({
      sourceApplicationId,
      targetVacancyId,
    }: {
      sourceApplicationId: string
      targetVacancyId: string
    }) =>
      vacancySuggestionService.compare(
        organization!.id,
        sourceApplicationId,
        targetVacancyId,
      ),
    onSuccess: () => {
      if (organization?.id) {
        queryClient.invalidateQueries({
          queryKey: vacancySuggestionKeys.all(organization.id),
        })
      }
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'Failed to run comparison'))
    },
  })
}

export function useSuggestCandidate() {
  const queryClient = useQueryClient()
  const organization = useCurrentOrganization()

  return useMutation({
    mutationFn: ({
      suggestionId,
      recruiterMessage,
      includeSalaryInMessage,
    }: {
      suggestionId: string
      recruiterMessage: string | null
      includeSalaryInMessage: boolean
    }) =>
      vacancySuggestionService.suggest(
        organization!.id,
        suggestionId,
        recruiterMessage,
        includeSalaryInMessage,
      ),
    onSuccess: () => {
      toast.success('Candidate suggested successfully. Email sent.')
      if (organization?.id) {
        queryClient.invalidateQueries({
          queryKey: vacancySuggestionKeys.all(organization.id),
        })
      }
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'Failed to send suggestion'))
    },
  })
}

export function useWithdrawSuggestion() {
  const queryClient = useQueryClient()
  const organization = useCurrentOrganization()

  return useMutation({
    mutationFn: (suggestionId: string) =>
      vacancySuggestionService.withdraw(organization!.id, suggestionId),
    onSuccess: () => {
      toast.success('Suggestion withdrawn.')
      if (organization?.id) {
        queryClient.invalidateQueries({
          queryKey: vacancySuggestionKeys.all(organization.id),
        })
      }
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'Failed to withdraw suggestion'))
    },
  })
}
